<template>
  <li>
    <modal
      v-if="showModal"
      class="feedback-modal">
      <div slot="header" class="title mb-2">
        <div class="d-flex align-center justify-space-between text">
          <div class="d-flex flex-column align-start justify-start modal-name mr-10" :class="`name-${item.slug}`">
            <h3><b>{{ item.customer.name | formatName(item.store.name.length) }}'s feedback</b></h3>
            <Tooltip 
              v-if="item.customer.name.length >= 25"
              :text="`${item.customer.name}'s feedback`"
              :target="`.modal-name.name-${item.slug}`"
              :show-delay="500"
              name="longNameTooltip"
              custom-class="long-name-tooltip"
            />
            <p class="text-left phone-number" v-if="!item.customer.member_id">
              <i class="fas fa-phone-square-alt"></i> {{formatPhone(item.customer.mobile)}}
            </p>
            <p class="text-left phone-number member-link" v-else @click="redirectToMemberProfile(item.customer.member_id)">
              <i class="fas fa-phone-square-alt"></i> <u class="phone-link">{{formatPhone(item.customer.mobile)}}</u>
            </p>
          </div>
          <div class="d-flex flex-column align-end justify-middle">
            <h4 class="text-right store-name-ellipsis">
              <fai :icon="['fas', 'home']" class="mr-1" />{{ item.store.name }}
            </h4>
            <h4 class="text-right">
              <fai :icon="['far', 'clock']" class="mr-1"/>{{formatDate(item.date)}}
            </h4>
          </div>
        </div>
        <div class="d-flex align-center justify-start">
          <hr style="margin: 10px; width: 100%;" />
        </div>
      </div>
      <div slot="body" class="body">
        <ul v-for="response in responseList" class="info-box mb-0">
          <li class="detail-item mb-2">
            <h4 class="modal-header">
              {{response.score_text}}
            </h4>
            <div class="info-box-number d-flex">
              <div class="d-flex justify-start align-center modal-header">
                <i class="score" 
                   :class="{'bg-green': parseInt(item[response.score]) > 4,
                            'bg-yellow': parseInt(item[response.score]) == 4,
                            'bg-red': parseInt(item[response.score]) < 4}">
                  <span class="nps-score-in-icon">{{item[response.score]}}
                  </span>
                </i>
              </div>
              <div class="d-flex align-center justify-start stars">
                <span v-for="index in 5" :key="index">
                  <fai :icon="['fas', 'check']" class="mr-1" 
                       :class="{
                         'text-lightgrey': item[response.score] < index,
                         'text-green': item[response.score] >= index 
                                       && item[response.score] > 4,
                         'text-yellow': item[response.score] >= index 
                                        && item[response.score] == 4,
                         'text-red': item[response.score] >= index 
                                     && item[response.score] < 4
                       }" />
                </span>
              </div>
            </div>
          </li>
          <li v-if="item[response.comment] != null" class="detailItem mb-2">
            <div class="info-box-comment">
              <h4 class="modal-header">
                {{response.comment_text}}
              </h4>
              <h5 class="d-flex align-center justify-start comment">
                {{item[response.comment].toLowerCase()}}
              </h5>
            </div>
          </li>
        </ul>
      </div>
      <div slot="action" class="modal-footer">
        <button class="action-button button" @click="itemClickHandler()">OK</button>
      </div>
    </modal>

    <div class="timeline-item box">
      <div class="d-flex justify-start align-center header">
        <i class="score" 
           :class="{'bg-green': parseInt(getScoreFromCommentType()) > 4,
                    'bg-yellow': parseInt(getScoreFromCommentType()) == 4,
                    'bg-red': parseInt(getScoreFromCommentType()) < 4}">
          <span class="nps-score-in-icon">{{ getScoreFromCommentType() }}</span>
        </i>
        <div class="d-flex flex-column mr-auto">
          <div class="d-flex justify-start">
            <h4 class="mx-1 my-0 customer-name">
              <a
                class="onhover no-underline"
                @click="itemClickHandler(item)"
              >
                {{item.customer.name.toLowerCase()}}
              </a>
            </h4>
          </div>
        </div>
        
        <div class="d-flex flex-column justify-end align-top shrink mr-2 mt-1">
          <div class="w-100 time small text-right">
            <fai :icon="['far', 'clock']" class="mr-1 mt-1"/>
            {{formatDate(item.date)}}
          </div>

          <div class="d-flex justify-end text-right">
            <span class=" small --badge --badge-info">{{item.store.name.toLowerCase()}}</span>
          </div>
        </div>
      </div>

      <div class="timeline-body mt-1">
        <p>{{ getCommentFromCommentType() }}</p>
        <div class="d-flex flex-row justify-end mx-2">
        </div>
      </div>

      <feedbackTagButtons
        :feedbackSlug="item.slug"
        :feedbackTags="item.user_tags"/>
    </div>
  </li>
  <!-- END timeline item -->
</template>

<script>
import modal from '../../shared/Modal'
import { Tooltip } from 'uiv'
import feedbackTagButtons from './FeedbackTagButtons'
import moment from 'moment'

export default {
  props: {
    item: [Object, Boolean],
    commentsType: [Boolean, String]
  },
  name: "TimelineItem",
  components: {
    modal,
    Tooltip,
    feedbackTagButtons
  },
  data() {
    return {
      showModal: false,
      responseList: {
        crs: {
          score_text: 'on a scale of 1 to 5 please rate your experience with our staff?',
          score: "crs_score",
          comment_text: 'we\'re sorry you were unhappy with our customer service.',
          comment: "crs_comment"
        },
        product: {
          score_text: 'on a scale of 1 to 5 please the quality of the products received?',
          score: "product_score",
          comment_text: 'we\'re sorry you were unhappy with our products.',
          comment: "product_comment"
        },
        nps: {
          score_text: 'how likely are you to refer us to your friends and family?',
          score: "nps_score",
          comment_text: 'do you have any additional feedback to help us improve your experience?',
          comment: "nps_comment"
        }
      }
    };
  },
  methods: {
    getScoreFromCommentType() {
      const { item, commentsType } = this;
      const scoreName = `${commentsType}_score`;

      if (scoreName && item[scoreName]) return item[scoreName];
      return item.nps_score;
    },
    getCommentFromCommentType() {
      const { item, commentsType } = this;
      const commentName = `${commentsType}_comment`;

      if (commentName && item[commentName]) return item[commentName].toLowerCase();
      return item.nps_comment !== null ? item.nps_comment : "no comment given.";
    },
    itemClickHandler() {
      this.showModal = !this.showModal
    },
    starColor(score, starNum) {
      const scoreValue = parseInt(score)
      if(starNum > scoreValue) return 'lightgrey';
      if (scoreValue > 4) return "#45f0b0";
      if (scoreValue == 4) return "#f0d845";
      if (scoreValue < 4) return "#f05345";
    },
    formatDate(date) {
      return moment(date, 'YYYY-MM-DD hh:mm:ss').format('ddd, MMM Do').toLowerCase()
    },
    formatPhone(phone) {
      return phone
        .replace(/[^0-9]/g, "")
        .replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, "+$1($2)$3-$4");
    },
    redirectToMemberProfile(member_id){
      window.location.href = `/members/${member_id}`
    },
  },
  filters: {
    formatName: (name, storeNameLength) => {
      const totalLength = name.length + Math.min(storeNameLength, 35)
      if (totalLength < 65) return name.toLowerCase()

      return (name.slice(0,62 - storeNameLength) + "...").toLowerCase()
    },
    formatStoreName: (name) => {
      if (name.length <= 33) return name.toLowerCase()

      return (name.slice(0,30) + "...").toLowerCase()
    }
  }
};
</script>
